import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DownloadIcon from '@mui/icons-material/Download';
import { Alert, Box, Button, CircularProgress, Grid, Typography, useTheme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useState } from 'react';
import { Accept, useDropzone } from 'react-dropzone';
import { useNotification } from '../../../context/NotificationContext';
import { useTranslation } from '../../../hooks/useTranslation';
import { Platform, PlatformImporter } from '../../../services/integration.types';
import { QUERY_KEYS } from '../../../utils/queryKeys';
import { useIntegrationMutations } from '../hooks/useIntegrationMutations';

interface IntegrationExchangeCsvFormComponent {
    platform: Platform;
    importer: PlatformImporter;
    onJobCreated: () => void;
}

const IntegrationExchangeCsvFormComponent: React.FC<IntegrationExchangeCsvFormComponent> = ({
    platform,
    importer,
    onJobCreated,
}) => {
    const { t } = useTranslation();
    const [file, setFile] = useState<File | null>(null);
    const { importCsv, isCsvImporting } = useIntegrationMutations();
    const { showNotification } = useNotification();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isQueryInvalidating, setIsQueryInvalidating] = useState(false);
    const theme = useTheme();
    const queryClient = useQueryClient();

    // Combined loading state to check if any operation is in progress
    const isLoading = isCsvImporting || isQueryInvalidating;

    const getAllowedFileTypes = (type: string): { accept: Accept; fileTypes: string } => {
        switch (type) {
            case 'csv':
                return {
                    accept: {
                        'text/csv': ['.csv'],
                    },
                    fileTypes: '.csv files',
                };
            case 'excel':
                return {
                    accept: {
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                        'application/vnd.ms-excel': ['.xls'],
                    },
                    fileTypes: '.xlsx or .xls files',
                };
            default:
                return {
                    accept: {
                        'text/csv': ['.csv'],
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                        'application/vnd.ms-excel': ['.xls'],
                    },
                    fileTypes: ' files',
                };
        }
    };

    const fileTypeConfig = getAllowedFileTypes(importer.type);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
            setFile(acceptedFiles[0]);
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: false,
        accept: fileTypeConfig.accept,
    });

    const handleSubmit = async () => {
        if (!file) return;

        // Clear any previous error messages
        setErrorMessage(null);

        importCsv(
            {
                importerUri: importer.uri,
                file: file,
            },
            {
                onSuccess: async (response: any) => {
                    const failedImport = response?.success === false;

                    setIsQueryInvalidating(true);
                    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INTEGRATIONS] });

                    if (failedImport) {
                        showNotification(t('integrations.exchange.csv.notifications.completedWithErrors'), 'error');
                        setErrorMessage(t('integrations.exchange.csv.notifications.completedWithErrors'));
                    } else {
                        // Calculate how much transactions were imported
                        const total =
                            response?.transactions -
                            response?.duplicate_transactions_found -
                            response?.cancelled_transactions_found -
                            response?.empty_timestamps -
                            response?.empty_values;

                        if (total <= 0) {
                            showNotification(t('integrations.exchange.csv.notifications.noTransactions'), 'error');
                            setErrorMessage(t('integrations.exchange.csv.notifications.noTransactions'));
                        } else {
                            showNotification(
                                t('integrations.exchange.csv.notifications.success', { total }),
                                'success'
                            );
                            // Clear any error messages on success
                            setErrorMessage(null);
                        }
                    }
                    setIsQueryInvalidating(false);
                    setFile(null);
                    // Wait briefly to let user see the notification before navigating away
                    setTimeout(() => {
                        onJobCreated();
                    }, 1500);
                },
                onError: (error) => {
                    // Show error notifications
                    showNotification(t('integrations.exchange.csv.notifications.error', { error }), 'error');
                    setErrorMessage(t('integrations.exchange.csv.notifications.error', { error }));
                },
            }
        );
    };

    if (!platform || !importer)
        return (
            <Typography variant="h6" gutterBottom>
                {t('integrations.exchange.selectIntegration')}
            </Typography>
        );

    return (
        <>
            <Grid container spacing={3} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
                <Grid item xs={12} md={6}>
                    <Box
                        {...getRootProps()}
                        sx={{
                            border: '2px dashed',
                            borderColor: isDragActive ? theme.palette.primary.main : theme.palette.divider,
                            borderRadius: 1,
                            p: 3,
                            textAlign: 'center',
                            cursor: 'pointer',
                            bgcolor: isDragActive ? 'rgba(25, 118, 210, 0.04)' : 'background.paper',
                            transition: 'all 0.2s',
                            '&:hover': {
                                borderColor: theme.palette.primary.main,
                                bgcolor: 'rgba(25, 118, 210, 0.04)',
                            },
                            ...(file && {
                                borderColor: theme.palette.success.main,
                                bgcolor: 'rgba(76, 175, 80, 0.04)',
                            }),
                        }}
                    >
                        <input {...getInputProps()} />
                        <CloudUploadIcon
                            sx={{
                                fontSize: 48,
                                color: theme.palette.primary.main,
                                mb: 2,
                            }}
                        />
                        <Typography variant="h6" gutterBottom>
                            {isDragActive
                                ? t('integrations.exchange.csv.dropActive')
                                : t('integrations.exchange.csv.dropInactive')}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {file
                                ? t('integrations.exchange.csv.selectedFile', { filename: file.name })
                                : ''}
                        </Typography>
                    </Box>
                    {file && (
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            onClick={handleSubmit}
                            disabled={isLoading}
                            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <FileUploadIcon />}
                            sx={{ mt: 2 }}
                        >
                            {isLoading
                                ? t('integrations.exchange.csv.uploading')
                                : t('integrations.exchange.csv.uploadFile')}
                        </Button>
                    )}
                    {errorMessage && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {errorMessage}
                        </Alert>
                    )}
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                            p: 2,
                            height: '100%',
                            bgcolor:
                                theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                            borderRadius: 1,
                        }}
                    >
                        {importer.customText ? (
                            <Typography
                                variant="body2"
                                dangerouslySetInnerHTML={{ __html: importer.customText }}
                                sx={{
                                    pb: 1,
                                    '& *': {
                                        color:
                                            theme.palette.mode === 'dark'
                                                ? theme.palette.text.primary + ' !important'
                                                : 'inherit',
                                    },
                                }}
                            />
                        ) : (
                            <>
                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
                                    {t('integrations.exchange.csv.uploadInstructions')}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {t('integrations.exchange.csv.step1')}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<DownloadIcon />}
                                    component="a"
                                    href="/coinfox_template.xlsx"
                                    download
                                    sx={{ mb: 2 }}
                                >
                                    {t('integrations.exchange.csv.downloadTemplate')}
                                </Button>
                                
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {t('integrations.exchange.csv.step2')}
                                </Typography>
                                
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {t('integrations.exchange.csv.step3')}
                                </Typography>
                            </>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </>
    );
};

export default IntegrationExchangeCsvFormComponent;
